import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Lock, ArrowRight, ArrowLeft,
  RotateCcw, Trophy, AlertCircle, Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '../api';

const QuizPlayer = ({ topicTitle, topicStatus, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [quizData, setQuizData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [previousAttempts, setPreviousAttempts] = useState([]);
  const [view, setView] = useState('quiz'); // 'quiz' | 'results' | 'history'

  const fetchQuiz = useCallback(async () => {
    try {
      const res = await api.get(`/student/quiz/${encodeURIComponent(topicTitle)}`);
      const data = res.data;
      if (data.locked) {
        setLocked(true);
        setQuizData(data.quiz);
      } else if (data.quiz?.questions) {
        setQuizData(data.quiz);
        setQuestions(data.quiz.questions);
        setAnswers(new Array(data.quiz.questions.length).fill(''));
        setPreviousAttempts(data.attempts || []);
      }
    } catch {
      toast.error('Failed to load quiz');
    }
    setLoading(false);
  }, [topicTitle]);

  useEffect(() => { fetchQuiz(); }, [fetchQuiz]);

  const selectAnswer = (idx, option) => {
    if (submitted) return;
    const newAnswers = [...answers];
    newAnswers[idx] = option;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    const unanswered = answers.filter(a => !a).length;
    if (unanswered > 0) {
      toast.error(`Please answer all ${unanswered} remaining question${unanswered > 1 ? 's' : ''}`);
      return;
    }
    try {
      const res = await api.post(`/student/quiz/${quizData.id}/submit`, { answers });
      setResult(res.data);
      setSubmitted(true);
      setView('results');
      toast.success(`Quiz completed! Score: ${res.data.score}/${res.data.total}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit quiz');
    }
  };

  const retakeQuiz = () => {
    setAnswers(new Array(questions.length).fill(''));
    setSubmitted(false);
    setResult(null);
    setCurrentQ(0);
    setView('quiz');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet-600" />
      </div>
    );
  }

  // Locked state
  if (locked) {
    return (
      <div className="text-center py-10" data-testid="quiz-locked">
        <Lock className="w-14 h-14 text-slate-300 mx-auto mb-3" />
        <p className="font-bold text-slate-700 text-lg">Quiz Locked</p>
        <p className="text-sm text-slate-400 mt-2">Complete this topic first to unlock the quiz</p>
        {quizData?.question_count && (
          <p className="text-xs text-slate-400 mt-1">{quizData.question_count} questions waiting</p>
        )}
      </div>
    );
  }

  // No quiz
  if (!quizData || !questions.length) {
    return (
      <div className="text-center py-10" data-testid="quiz-none">
        <AlertCircle className="w-14 h-14 text-slate-300 mx-auto mb-3" />
        <p className="font-bold text-slate-700 text-lg">No Quiz Available</p>
        <p className="text-sm text-slate-400 mt-2">Your teacher hasn't created a quiz for this topic yet</p>
      </div>
    );
  }

  // Results view
  if (view === 'results' && result) {
    const pct = result.percentage;
    const isPassing = pct >= 70;
    return (
      <div className="space-y-5" data-testid="quiz-results">
        {/* Score Hero */}
        <div className={`rounded-2xl p-6 text-center ${isPassing ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
          <Trophy className={`w-12 h-12 mx-auto mb-2 ${isPassing ? 'text-emerald-500' : 'text-amber-500'}`} />
          <p className="text-3xl font-black text-slate-900">{result.score}/{result.total}</p>
          <p className={`text-lg font-bold ${isPassing ? 'text-emerald-700' : 'text-amber-700'}`}>{pct}% — {isPassing ? 'Great Job!' : 'Keep Practicing'}</p>
        </div>

        {/* Question-by-question */}
        <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-1">
          {result.results.map((r, idx) => (
            <div key={idx} className={`rounded-xl p-4 border ${r.is_correct ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid={`result-q-${idx}`}>
              <div className="flex items-start gap-2">
                {r.is_correct
                  ? <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />}
                <div className="flex-1">
                  <p className="font-bold text-sm text-slate-900">Q{idx + 1}: {r.question}</p>
                  <p className="text-xs mt-1">
                    <span className="text-slate-500">Your answer: </span>
                    <span className={r.is_correct ? 'text-emerald-700 font-bold' : 'text-red-600 font-bold line-through'}>{r.student_answer}</span>
                  </p>
                  {!r.is_correct && (
                    <p className="text-xs mt-0.5">
                      <span className="text-slate-500">Correct: </span>
                      <span className="text-emerald-700 font-bold">{r.correct_answer}</span>
                    </p>
                  )}
                  {r.explanation && (
                    <p className="text-xs mt-1 text-slate-500 italic">{r.explanation}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button onClick={retakeQuiz} variant="outline" className="flex-1 text-sm font-bold" data-testid="retake-quiz-btn">
            <RotateCcw className="w-4 h-4 mr-1.5" /> Retake Quiz
          </Button>
          <Button onClick={onClose} className="flex-1 bg-slate-800 hover:bg-slate-900 text-sm font-bold" data-testid="close-results-btn">
            Done
          </Button>
        </div>

        {/* Previous Attempts */}
        {previousAttempts.length > 0 && (
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-bold text-slate-500 mb-2">Previous Attempts ({previousAttempts.length})</p>
            <div className="space-y-1.5">
              {previousAttempts.map((a, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 text-xs">
                  <span className="text-slate-600">{new Date(a.completed_at).toLocaleDateString()}</span>
                  <span className="font-bold text-slate-800">{a.score}/{a.total} ({a.percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Quiz-taking view
  const q = questions[currentQ];
  const answered = answers.filter(a => a).length;
  const progressPct = Math.round((answered / questions.length) * 100);

  return (
    <div className="space-y-5" data-testid="quiz-player">
      {/* Progress Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-slate-500">Question {currentQ + 1} of {questions.length}</span>
          <span className="text-xs font-bold text-violet-600">{answered}/{questions.length} answered</span>
        </div>
        <Progress value={progressPct} className="h-2" />
      </div>

      {/* Question */}
      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200" data-testid={`quiz-question-${currentQ}`}>
        <p className="font-bold text-slate-900 text-base leading-relaxed">{q.question}</p>
      </div>

      {/* Options */}
      <div className="space-y-2.5">
        {q.options.map((opt, oi) => {
          const isSelected = answers[currentQ] === opt;
          return (
            <button
              key={oi}
              onClick={() => selectAnswer(currentQ, opt)}
              className={`w-full text-left px-4 py-3.5 rounded-xl border-2 transition-all text-sm font-semibold flex items-center gap-3
                ${isSelected
                  ? 'bg-violet-600 text-white border-violet-600 shadow-md'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-violet-300 hover:bg-violet-50'
                }`}
              data-testid={`option-${currentQ}-${oi}`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black flex-shrink-0
                ${isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {String.fromCharCode(65 + oi)}
              </span>
              {opt}
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button
          onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
          disabled={currentQ === 0}
          variant="outline"
          size="sm"
          className="text-xs font-bold"
          data-testid="prev-question-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Previous
        </Button>

        {/* Question dots */}
        <div className="flex items-center gap-1">
          {questions.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentQ(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${
                i === currentQ ? 'bg-violet-600 scale-125' : answers[i] ? 'bg-violet-300' : 'bg-slate-200'
              }`}
              data-testid={`question-dot-${i}`}
            />
          ))}
        </div>

        {currentQ < questions.length - 1 ? (
          <Button
            onClick={() => setCurrentQ(currentQ + 1)}
            size="sm"
            className="bg-violet-600 hover:bg-violet-700 text-xs font-bold"
            data-testid="next-question-btn"
          >
            Next <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-xs font-bold"
            data-testid="submit-quiz-btn"
          >
            Submit Quiz
          </Button>
        )}
      </div>

      {/* Attempt History Link */}
      {previousAttempts.length > 0 && (
        <div className="border-t border-slate-200 pt-3">
          <button onClick={() => setView('history')} className="text-xs text-violet-600 font-bold hover:underline flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" /> View {previousAttempts.length} previous attempt{previousAttempts.length > 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* History View (inline) */}
      {view === 'history' && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-slate-700">Attempt History</p>
            <button onClick={() => setView('quiz')} className="text-xs text-violet-600 font-bold">Back to Quiz</button>
          </div>
          {previousAttempts.map((a, i) => (
            <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-slate-100 text-xs">
              <div>
                <span className="font-bold text-slate-700">Attempt {previousAttempts.length - i}</span>
                <span className="text-slate-400 ml-2">{new Date(a.completed_at).toLocaleString()}</span>
              </div>
              <span className={`font-black ${a.percentage >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>
                {a.score}/{a.total} ({a.percentage}%)
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuizPlayer;
